import type { CasinoSharedRootState } from '~/components/casino-shared/ducks/types';
import type { GameSlot, GameState, GamePositions } from '~/components/casino-shared/game-loader/ducks/types';
import { type GameLoaderJSONResponse } from '~/components/casino-shared/game-loader/use-game-messaging';
import { REDUCER_NAME } from './constants';
import { createSelector } from 'reselect';
import { selectEnableCasinoMultiGame, selectEnableLiveCasinoMultiGame } from '~/components/brand/ducks/selectors.js';
import { isBridgeAvailable } from '~/components/app-bridge';

export const currentGameSlotSelector = (state: CasinoSharedRootState) =>
	Object.entries(state[REDUCER_NAME]?.gameSlots || {}).find(
		([, gameState]) => gameState.position === 'current'
	)?.[0] as GameSlot | undefined;

// Helper function to resolve game slot
const resolveGameSlot = (state: CasinoSharedRootState, gameSlot?: GameSlot): GameSlot => {
	if (gameSlot) return gameSlot;

	const currentGameSlot = currentGameSlotSelector(state);

	return currentGameSlot ?? 1;
};
// Generic helper to preserve the specific property type instead of widening to a union of all GameState values
const getGameSlotProperty = <P extends keyof GameState>(
	state: CasinoSharedRootState,
	part: P,
	gameSlot?: GameSlot
): GameState[P] | undefined => {
	return state[REDUCER_NAME]?.gameSlots?.[resolveGameSlot(state, gameSlot)]?.[part];
};
export const hydratedSelector = (state: CasinoSharedRootState, gameSlot?: GameSlot) =>
	getGameSlotProperty(state, 'hydrated', gameSlot);

export const gameLoaderResponseSelector = (state: CasinoSharedRootState, gameSlot?: GameSlot) =>
	getGameSlotProperty(state, 'gameLoaderResponse', gameSlot);

export const gameLoaderRequestPendingSelector = (state: CasinoSharedRootState, gameSlot?: GameSlot) =>
	getGameSlotProperty(state, 'gameLoaderRequestPending', gameSlot);

export const statusSelector = (state: CasinoSharedRootState, gameSlot?: GameSlot) =>
	getGameSlotProperty(state, 'status', gameSlot);

export const loadAsSelector = (state: CasinoSharedRootState, gameSlot?: GameSlot) =>
	getGameSlotProperty(state, 'loadAs', gameSlot);

export const gameModeSelector = (state: CasinoSharedRootState, gameSlot?: GameSlot) =>
	getGameSlotProperty(state, 'gameMode', gameSlot);

export const errorCodeSelector = (state: CasinoSharedRootState, gameSlot?: GameSlot) =>
	getGameSlotProperty(state, 'errorCode', gameSlot);

export const errorDescriptionSelector = (state: CasinoSharedRootState, gameSlot?: GameSlot) =>
	getGameSlotProperty(state, 'errorDescription', gameSlot);

export const currentGameSelector = (state: CasinoSharedRootState): GameState | undefined => {
	const currentGameSlot = currentGameSlotSelector(state);
	return state[REDUCER_NAME]?.gameSlots?.[currentGameSlot ?? 1];
};

export const gameSelector = (gameSlot: GameSlot) => (state: CasinoSharedRootState) => {
	const resolvedSlot = resolveGameSlot(state, gameSlot);
	return state[REDUCER_NAME]?.gameSlots?.[resolvedSlot];
};

export const gameSlotPositionsSelector = (state: CasinoSharedRootState): GamePositions => {
	const positions: GamePositions = {};
	Object.entries(state[REDUCER_NAME]?.gameSlots ?? {}).forEach(([_slot, gameState]) => {
		if (gameState.position && gameState.gameData?.code) {
			positions[gameState.position] = gameState.gameData.code;
		}
	});
	return positions;
};

// export const getAllGameSlotsSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameSlots;

// memoized selector for all game slots to avoid redundant re-renders
export const getAllGameSlotsSelector = createSelector(
	[(state: CasinoSharedRootState) => state[REDUCER_NAME]?.gameSlots],
	gameSlots => gameSlots
);

// Memoized selector for all gameLoaderResponse fields by slot
export const getAllGameLoaderResponsesSelector = createSelector(
	[getAllGameSlotsSelector],
	gameSlots =>
		new Map<GameSlot, GameLoaderJSONResponse>(
			Object.entries(gameSlots)
				.map(([key, slot]) => [Number(key) as GameSlot, slot.gameLoaderResponse as GameLoaderJSONResponse])
				.filter((entry): entry is [GameSlot, GameLoaderJSONResponse] => entry[1] !== null)
		)
);

export const cleanUpListenersSelector = (state: CasinoSharedRootState) => state[REDUCER_NAME]?.cleanUpListeners;

export interface EnableMultiGameEnvProps {
	isDesktop: boolean;
}

const multiGameEnvSelector = (_: CasinoSharedRootState, env: EnableMultiGameEnvProps) => env;

export const enableMultiGameSelector = createSelector(
	[currentGameSelector, selectEnableCasinoMultiGame, selectEnableLiveCasinoMultiGame, multiGameEnvSelector],
	(
		currentGame,
		enableCasinoMultiGame: boolean,
		enableLiveCasinoMultiGame: boolean,
		{ isDesktop }: EnableMultiGameEnvProps
	) => {
		const isInMobileApp = isBridgeAvailable();

		if (isInMobileApp) return false; // mobile app is disabled for now
		if (isDesktop) return false; // mobile only for now
		if (currentGame?.gameMode === 'fun') return false;
		if (currentGame?.gameData?.categoryCode === 'CASINO_LIVE_CASINO') {
			return !!enableLiveCasinoMultiGame;
		}
		if (currentGame?.gameData?.categoryCode !== 'CASINO_LIVE_CASINO') {
			return !!enableCasinoMultiGame;
		}
		return false;
	}
);
